import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { API_BASE } from "./apiBase";

// Module-level token mirror so the default queryFn and apiRequest can
// attach the Bearer header without pulling React context. AuthProvider
// writes it in the same synchronous step as its token state (publishToken),
// never from an effect: an effect lands after the effects of every component
// below it, which is exactly where the first signed requests come from.
let currentAuthToken: string | null = null;
export function setAuthToken(token: string | null) {
  currentAuthToken = token;
}
/** True when a bearer token is currently available (user is signed in). */
export function hasAuthToken(): boolean {
  return currentAuthToken != null && currentAuthToken !== "";
}
/**
 * The raw token, for the one caller that cannot send a header: a browser
 * WebSocket, which has no API for them. Everything else must use apiRequest or
 * the default queryFn so the token stays out of URLs — see raidHubClient.
 */
export function getAuthToken(): string | null {
  return currentAuthToken;
}
function authHeaders(extra?: HeadersInit): Headers {
  const h = new Headers(extra);
  if (currentAuthToken) h.set("Authorization", `Bearer ${currentAuthToken}`);
  return h;
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers = authHeaders();
  if (data) headers.set("Content-Type", "application/json");
  const res = await fetch(`${API_BASE}${url}`, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

/**
 * Like apiRequest but returns the raw Response without throwing on non-2xx.
 * Used by ingest paths that need to react to 429 (quota) / 413 (too large)
 * with custom UI copy rather than a blanket toast.
 */
export async function apiRequestRaw(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers = authHeaders();
  if (data !== undefined) headers.set("Content-Type", "application/json");
  return fetch(`${API_BASE}${url}`, {
    method,
    headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(`${API_BASE}${(queryKey as readonly unknown[]).join("/")}`, {
      headers: authHeaders(),
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
